import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useNavigate } from 'react-router-dom';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { formatDate } from '../../helpers/UtilsFunctions';
import { format } from 'date-fns';
import { 
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarIcon from '../../assets/Calendar.svg';
import TargetIcon from '../../assets/Target.svg';
import EditButtonIcon from '../../assets/Edit_Button.svg';
import DeleteButtonIcon from '../../assets/Delete_Button.svg';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const ProjectComponent = ({ project, onEdit, onDelete }) => {
  const { mode } = useThemeMode();
  const { hasEditPermission, hasDeletePermission } = usePermissions();
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');

  // Helper function to get the status value from various possible field names
  const getProjectStatus = () => {
    // Log the full project object to debug
    console.log('Full project object:', project);
    
    // Check various possible status field names
    const status = project.status || project.project_status || 'active';
    
    // Convert to lowercase to handle case sensitivity
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : 'active';
    
    console.log('Normalized status for project', project.title, ':', normalizedStatus);
    
    return normalizedStatus;
  };

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (userData && userData.company_detail) {
        setCompanyLogo(userData.company_detail.company_logo || null);
        setCompanyName(userData.company_detail.name || '');
      }
    } catch (e) {
      setCompanyLogo(null);
      setCompanyName('');
    }
  }, []);

  // Handle project card click to navigate to tasks
  const handleProjectClick = (e) => {
    // Don't navigate if clicking on action buttons or their children
    if (e.target.closest('[data-action-button]')) {
      return;
    }
    navigate(`/project/${project.id}/tasks`);
  };

  // Handle action button clicks to prevent navigation
  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit && onEdit(project);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete && onDelete(project);
  };

  // Get initials from username
  const getInitials = (username) => {
    if (!username) return '?';
    return username.charAt(0).toUpperCase();
  };



  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#FFC107'; // Yellow for active
      case 'planned':
        return '#FF9800'; // Orange for planned
      case 'completed':
        return '#4CAF50'; // Green for completed
      case 'on_hold':
        return '#9E9E9E'; // Gray for on hold
      default:
        return '#FFC107'; // Default to yellow
    }
  };

  // Get status display text
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'planned':
        return 'Planned';
      case 'completed':
        return 'Completed';
      case 'on_hold':
        return 'On Hold';
      default:
        return 'Active';
    }
  };

  return (
    <div 
      onClick={handleProjectClick}
      style={{
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
        position: 'relative',
        width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = mode === 'dark' 
          ? '0 6px 25px rgba(0, 0, 0, 0.4)' 
          : '0 6px 25px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)';
      }}
    >
      {/* Header Section */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        {/* Company Logo and Project Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          {/* Company Logo */}
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '8px',
            backgroundColor: mode === 'dark' ? '#3a3a3a' : '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {companyLogo ? (
              <img
                src={getProfileCompanyPicUrl(companyLogo)}
                alt="Company Logo"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Replace the image with a fallback avatar when it fails to load
                  const fallbackDiv = document.createElement('div');
                  fallbackDiv.style.cssText = `
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    background-color: #26C3C0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #FFFFFF;
                    font-size: 18px;
                    font-weight: 600;
                  `;
                  fallbackDiv.textContent = companyName ? companyName.charAt(0).toUpperCase() : 'C';
                  e.target.parentNode.replaceChild(fallbackDiv, e.target);
                }}
              />
            ) : (
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#FFFFFF',
                backgroundColor: '#26C3C0',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {companyName ? companyName.charAt(0).toUpperCase() : 'C'}
              </span>
            )}
          </div>
          
          {/* Project Title and Description */}
          <div style={{ flex: 1, minWidth: 0, marginRight: '100px' }}>
            <h3 
              title={project.title}
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                margin: '0 0 4px 0',
                fontFamily: 'Urbanist, Arial, sans-serif',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {project.title}
            </h3>
            <p style={{
              fontSize: '13px',
              color: mode === 'dark' ? '#999' : '#666',
              margin: 0,
              fontFamily: 'Urbanist, Arial, sans-serif',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Created By: {project.created_by_username || 'Unknown'}
            </p>
          </div>
        </div>

        {/* Status Tag */}
        <div style={{
          backgroundColor: getStatusColor(getProjectStatus()),
          color: '#FFFFFF',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          fontFamily: 'Urbanist, Arial, sans-serif',
          flexShrink: 0,
          position: 'absolute',
          top: '20px',
          right: '20px'
        }}>
          {getStatusText(getProjectStatus())}
        </div>
      </div>

      {/* Progress Section */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{
            fontSize: '14px',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Progress
          </span>
          <span style={{
            fontSize: '14px',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '500'
          }}>
            {project.progress_percentage || 0}% Complete
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: mode === 'dark' ? '#444' : '#e0e0e0',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${project.progress_percentage || 0}%`,
            height: '100%',
            backgroundColor: '#4CAF50',
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Date Information */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1
        }}>
          <img 
            src={CalendarIcon} 
            alt="Start Date" 
            style={{ 
              width: '16px', 
              height: '16px',
              filter: mode === 'dark' ? 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.8) contrast(1)' : 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.6) contrast(1)'
            }} 
          />
          <span style={{
            fontSize: '13px',
            color: mode === 'dark' ? '#CCCCCC' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
                         Start: {formatDate(project.start_date) || 'Not set'}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1
        }}>
          <img 
            src={TargetIcon} 
            alt="End Date" 
            style={{ 
              width: '16px', 
              height: '16px',
              filter: mode === 'dark' ? 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.8) contrast(1)' : 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.6) contrast(1)'
            }} 
          />
          <span style={{
            fontSize: '13px',
            color: mode === 'dark' ? '#CCCCCC' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
                         End: {formatDate(project.end_date) || 'Not set'}
          </span>
        </div>
      </div>

      {/* Description Section */}
      <div style={{
        marginBottom: '16px',
        position: 'relative',
        backgroundColor: mode === 'dark' ? '#171b1b' : '#f8f9fa',
        borderRadius: '8px',
        padding: '12px'
      }}>
        {/* Description Label */}
        <div style={{
          fontSize: '10px',
          fontWeight: '600',
          color: mode === 'dark' ? '#9e9e9e' : '#666666',
          fontFamily: 'Urbanist, Arial, sans-serif',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Description
        </div>
        
        {/* Description Content */}
        <>
          <style>
            {`
              .project-description-scroll::-webkit-scrollbar {
                display: none;
              }
              .project-description-scroll,
              .project-description-scroll * {
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll p {
                margin: 0 0 8px 0 !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll ul, .project-description-scroll ol {
                margin: 0 0 8px 0 !important;
                padding-left: 20px !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll li {
                margin: 0 0 4px 0 !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll strong {
                font-weight: 600 !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll em {
                font-style: italic !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll span {
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
              .project-description-scroll div {
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
                color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
              }
            `}
          </style>
          <div 
            className="project-description-scroll"
            style={{
              fontSize: '14px !important',
              color: mode === 'dark' ? '#CCCCCC !important' : '#666666 !important',
              fontFamily: '"Urbanist", Arial, sans-serif !important',
              lineHeight: '1.4 !important',
              overflow: 'auto',
              height: '78px', // Fixed height for exactly 4 rows (14px * 1.4 * 4 = ~78px)
              wordBreak: 'break-word',
              textAlign: 'justify',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingRight: '4px'
            }}
            dangerouslySetInnerHTML={{
              __html: project.description || 'No description available'
            }}
          />
        </>
      </div>

      {/* Workers and Actions Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Worker Avatars */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(project.assigned_users || []).slice(0, 4).map((user, index) => (
              <div
                key={user.id}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: mode === 'dark' ? '#26C3C0' : '#009391',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  border: `2px solid ${mode === 'dark' ? '#2a2a2a' : '#ffffff'}`,
                  marginLeft: index > 0 ? '-8px' : '0',
                  zIndex: (project.assigned_users || []).length - index,
                  overflow: 'hidden'
                }}
              >
                {user.profile_pic ? (
                  <img
                    src={getProfilePicUrl(user.profile_pic)}
                    alt={user.full_name || user.username}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // Replace the image with a fallback avatar when it fails to load
                      const fallbackDiv = document.createElement('div');
                      fallbackDiv.style.cssText = `
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        background-color: #26C3C0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #FFFFFF;
                        font-size: 14px;
                        font-weight: 600;
                      `;
                      fallbackDiv.textContent = getInitials(user.username);
                      e.target.parentNode.replaceChild(fallbackDiv, e.target);
                    }}
                  />
                ) : (
                  getInitials(user.username)
                )}
              </div>
            ))}
            {(project.assigned_users || []).length > 4 && (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: mode === 'dark' ? '#444' : '#e0e0e0',
                color: mode === 'dark' ? '#FFFFFF' : '#666666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Urbanist, Arial, sans-serif',
                border: `2px solid ${mode === 'dark' ? '#2a2a2a' : '#ffffff'}`,
                marginLeft: '-8px',
                zIndex: 1
              }}>
                <PersonIcon style={{ fontSize: '16px' }} />
              </div>
            )}
          </div>
          
          <span style={{
            fontSize: '13px',
            color: mode === 'dark' ? '#CCCCCC' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {(() => {
              const count = project.assigned_users ? project.assigned_users.length : 0;
              if (count === 0) return '0 Work';
              if (count === 1) return '1 Worker';
              return `${count} Workers`;
            })()}
          </span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasEditPermission('task') && (
            <button
              onClick={handleEditClick}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              data-action-button
            >
              <img 
                src={EditButtonIcon} 
                alt="Edit" 
                style={{ 
                  width: '36px', 
                  height: '36px'
                }} 
              />
            </button>
          )}
          
          {hasDeletePermission('task') && (
            <button
              onClick={handleDeleteClick}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              data-action-button
            >
              <img 
                src={DeleteButtonIcon} 
                alt="Delete" 
                style={{ 
                  width: '36px', 
                  height: '36px'
                }} 
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectComponent;
